<template>
  <b-modal :active.sync="isModalActive" scroll="clip" width="65%">
    <b-loading :is-full-page="false" v-model="isLoading" :can-cancel="false"></b-loading>
    <div class="modal-card">
      <!-- HEADER -->
      <header class="modal-card-head flex flex-col gap-3">
        <!-- Línea 1: Título + Buscador -->
        <div class="flex items-center justify-between w-full gap-4">
          <h2 class="text-sm font-medium text-gray-800 uppercase tracking-wide">
            <b>{{ $t('file_explorer') }}</b>
          </h2>
          <!-- Buscador -->
          <div class="relative w-72">
            <input v-model="searchQuery"
              type="text"
              :placeholder="$t('search_folder_or_file')"
              class="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <svg
              class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
            </svg>
          </div>
        </div>
        <!-- Línea 2: Breadcrumb -->
        <nav class="flex text-sm text-gray-500 space-x-1 items-center justify-start w-full">
          <span
            class="hover:text-blue-600 cursor-pointer text-gray-600"
            @click="goToBreadcrumb(-1)"
          ><i class="fas fa-home mr-1"></i>{{ $t('file_explorer_home') }}</span>
          <template v-for="(crumb, index) in breadcrumbs">
            <span>/</span>
            <b-tooltip
              :label="crumb.name"
              multilined
            >
              <span
                style="max-width: 150px;"
                class="text-gray-600 truncate inline-block align-bottom"
                :class="{ 'hover:underline cursor-pointer': index !== breadcrumbs.length - 1, 'text-blue-600 font-medium': index === breadcrumbs.length - 1 }"
                @click="index !== breadcrumbs.length - 1 && goToBreadcrumb(index)"
                :title="crumb.name"
              >{{ crumb.name }}</span>
            </b-tooltip>
          </template>
        </nav>
      </header>

      <!-- BODY con fondo blanco liso -->
      <section class="modal-card-body" ref="scrollBody" @scroll="onBodyScroll">
        <div class="px-5 py-5">

          <!-- Sección de Carpetas -->
          <div v-if="folders.length > 0" class="mb-6">
            <h3 class="text-sm font-medium text-gray-500 uppercase mb-2">{{ $t('folders') }}</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <!-- Carpeta individual -->
              <div
                v-for="(folder, index) in folders.filter(f => f.visible)"
                :key="'folder_activity_' + index"
                class="flex items-start gap-4 p-4 rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:bg-gray-50 transition-all duration-200 cursor-pointer group"
                @click="openFolder(folder)"
              >
                <div class="text-2xl mt-1">
                  <i class="fas fa-folder" :class="folder.type === 'folder' ? 'text-gray-400' : 'text-blue-300'"></i>
                </div>
                <div class="flex-1">
                  <p class="font-semibold text-gray-900 text-base break-words whitespace-normal group-hover:underline">
                    {{ folder.name }}
                  </p>
                  <ul class="flex flex-wrap text-sm text-gray-500 mt-1 space-x-2">
                    <li>
                      <span v-if="folder.type === 'activity'">{{ $t('activity') }}</span>
                      <span v-else-if="folder.type === 'subactivity'">{{ $t('subactivity') }}</span>
                      <span v-else>{{ $t('folder') }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Sección de Archivos -->
          <div v-if="folderFiles.length > 0">
            <h3 class="text-sm font-medium text-gray-500 uppercase mb-2">{{ $t('files') }}</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div v-for="(file, index) in folderFiles.filter(f => f.visible)"
                @click="hanldeSelectFile(file)"
                class="relative group rounded-lg bg-white shadow hover:bg-gray-50 cursor-pointer overflow-hidden"
                :class="{
                  'border-blue-light border bg-blue-lighter': selectedFile && selectedFile.document_id === file.document_id,
                  'bg-white hover:bg-gray-50': selectedFile && selectedFile.document_id !== file.document_id
                }"
              >
                <!-- Imagen -->
                <div class="w-full bg-gray-100 dark:bg-neutral-700 flex items-center justify-center">
                  <img
                    :src="file.file.thumbnail || asset('public' + fallbackImage)"
                    alt="Folder Icon"
                    class="w-full h-full max-h-[180px] p-4 object-contain"
                    @error="onImageError"
                  />
                </div>

                <!-- Nombre -->
                <div class="px-4 py-4 text-center"
                  :class="{
                    'bg-blue-light': selectedFile && selectedFile.document_id === file.document_id,
                    'bg-white hover:bg-gray-50': selectedFile && selectedFile.document_id !== file.document_id
                  }"
                >
                  <p class="text-sm font-medium break-words whitespace-normal"
                    :class="{
                      'text-white': selectedFile && selectedFile.document_id === file.document_id,
                      'text-gray-700': selectedFile && selectedFile.document_id !== file.document_id
                    }"
                    :title="file.name"
                  >
                    {{ file.name }}
                  </p>

                  <ul class="flex justify-center gap-1 text-xs mt-0.5 truncate"
                    :class="{
                      'text-white': selectedFile && selectedFile.document_id === file.document_id,
                      'text-gray-500': selectedFile && selectedFile.document_id !== file.document_id
                    }"
                  >
                    <li>{{ fileTypeLabel(file) }}</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Indicador de carga diferida: aparece mientras se trae la
                 siguiente página de archivos al hacer scroll. -->
            <div v-if="loadingMore" class="flex justify-center items-center py-6 text-gray-400">
              <i class="fas fa-spinner fa-spin mr-2"></i>
              <span class="text-sm">{{ $t('loading_more_files') }}</span>
            </div>
          </div>

          <!-- Mensaje de vacío -->
          <div v-if="folders.length === 0 && folderFiles.length === 0 && breadcrumbs.length > 0 && !isLoading" class="flex flex-col items-center justify-center py-16 text-center">
            <i class="fas fa-folder-open text-6xl text-gray-300 mb-4"></i>
            <h3 class="text-lg font-medium text-gray-600 mb-2">{{ $t('folder_is_empty') }}</h3>
            <p class="text-sm text-gray-400">{{ $t('no_folders_or_files_here') }}</p>
          </div>
        </div>
      </section>

      <!-- FOOTER sin fondo gris, solo borde superior -->
      <footer class="modal-card-foot">
        <v-button v-if="!processingData" color="grey" icon="door-closed" @click="hide">{{ $t('close') }}</v-button>
        <div class="flex items-center space-x-2">
          <v-button v-if="!processingData" @click="saveActivityImage" icon="plus">{{ $t('upload_image') }}</v-button>
          <v-button-processing v-if="processingData" />
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import axios from "axios";
import SelectResource from './select-resource.vue';

export default {
  name: "select-resource-from-activity-modal",

  components: {
    SelectResource
  },

  props: {
    activities: {
      type: Array,
      required: true,
    },
    drawings: {
      type: Array,
      required: true,
    },
    report_sections: {
      type: Array,
      required: true,
    },
    fallbackImage: {
      type: String,
      default: '/img_placeholder.png'
    }
  },

  data() {
    return {
      processingData: false,
      isModalActive: false,
      isLoading: false,

      root: [],
      folders: [],
      folderFiles: [],
      breadcrumbs: [],
      selectedFile: null,
      currentIdActivity: null,

      // Carga diferida de archivos (scroll infinito). Las CARPETAS se traen
      // completas en la primera llamada; los ARCHIVOS se paginan con per_page
      // alto y se van agregando al hacer scroll. `currentDocFolder` guarda la
      // carpeta abierta para poder pedir las páginas siguientes del mismo nivel.
      filePage: 1,
      filePerPage: 60,
      totalFiles: 0,
      loadingMore: false,
      currentDocFolder: null,

      searchQuery: '',
      debounceTimeout: null,

      activityImage: {
        tabIndex: 0,
        imageIndex: 0,
        url: '',
        activity_id: null,
        subactivity_id: null,
        document_id: null,
        subactivities: [],
        documents: [],
        type: 'image'
      },
    }
  },

  computed: {
    isDrawing() {
      return this.activityImage.type === 'drawing';// || this.activityImage.type === 'drawingActivity';
    }
  },

  watch: {
    activities: {
      handler(newActivities) {
        this.isLoading = true;
        this.root = newActivities.map(activity => ({
          ...activity,
          visible: true,
          type: 'activity'
        }));
        this.folders = this.root; // Inicialmente, las carpetas son las actividades
        this.breadcrumb = [];
        this.isLoading = false;
      },
      immediate: true, // para que se ejecute también al inicio
      deep: false
    },

    'activityImage.type'(newType, oldType) {
      console.log(`Tipo cambiado: de ${oldType} a ${newType}`);
      this.breadcrumb = [];
      // Reset de la carga diferida al cambiar de modo (drawings no paginan).
      this.currentDocFolder = null;
      this.totalFiles = 0;
      this.filePage = 1;
      if (newType === 'drawing') {
        this.folders = [];
        this.folderFiles = this.drawings.map(drawing => ({
          ...drawing,
          visible: true
        }));
      } else {
        this.folders = this.root;
        this.folderFiles = [];
      }
    },

    searchQuery(newValue) {
      clearTimeout(this.debounceTimeout); // Cancelar anteriores

      this.debounceTimeout = setTimeout(() => {
        this.performSearch(newValue);
      }, 400); // Esperar 400ms después de la última tecla
    },

    isModalActive(newValue) {
      // Resetear todo cuando se cierra el modal (click fuera, ESC, etc.)
      if (!newValue) {
        this.resetModal();
      }
    }
  },

  created() {

  },

  mounted() {
  },

  methods: {
    performSearch(query) {
      this.folders.forEach(f => {
        f.visible = f.name.toLowerCase().includes(query.toLowerCase());
      });

      this.folderFiles.forEach(f => {
        f.visible = f.name.toLowerCase().includes(query.toLowerCase());
      });
    },

    hanldeSelectFile(file) {
      this.selectedFile = file;
    },

    // Tipo de archivo a mostrar bajo el nombre: extensión del nombre (PDF, JPG...)
    // o, si no tiene, el subtipo del mime_type. Genérico si no hay ninguno.
    fileTypeLabel(file) {
      const name = (file && file.name) || '';
      const dot = name.lastIndexOf('.');
      if (dot > 0 && dot < name.length - 1) {
        return name.substring(dot + 1).toUpperCase();
      }
      const mime = (file && file.mime_type) || '';
      if (mime.includes('/')) {
        return mime.split('/').pop().toUpperCase();
      }
      return this.$t('file');
    },

    onImageError (event) {
      event.target.src = this.asset('public' + this.fallbackImage);
    },

    async openFolder(folder) {
      try {
        this.isLoading = true;
        this.searchQuery = ''; // Limpiar el buscador al navegar por las migas de pan

        // Reset de la paginación de archivos al entrar a una carpeta nueva.
        this.filePage = 1;
        this.totalFiles = 0;
        this.currentDocFolder = folder;

        if (!this.breadcrumbs.some(crumb => crumb.id === folder.id)) {
          this.breadcrumbs.push({ id: folder.id, name: folder.name, type: folder.type });
        }

        if (folder.type == 'activity' || folder.type == 'subactivity') {
          this.currentIdActivity = folder.id;
        }

        // Subactividades solo si el nodo es actividad/subactividad. En carpetas
        // de documentos (weekly photos, etc.) folder.id es DocumentFolder, no
        // Activity: llamar a /subactivities devolvía 200 + success:false y un
        // toast falso de "error_loading_data" aunque getDocuments fuera OK.
        let subactivitiesMapped = [];
        if (folder.type === 'activity' || folder.type === 'subactivity') {
          const subactivities = await this.getSubactivities(folder.id);
          subactivitiesMapped = Array.isArray(subactivities) && subactivities.length
            ? subactivities.map(item => ({
                ...item,
                visible: true,
                type: 'subactivity'
              }))
            : [];
        }

        // Primera página de archivos. El backend ya devuelve solo imágenes/PDFs.
        const response = await this.getDocuments(folder, 1)
        const files = Array.isArray(response.documents) ? response.documents : [];
        this.totalFiles = typeof response.total === 'number' ? response.total : files.length;

        const documentFoldersMapped = Array.isArray(response.folders) && response.folders.length
          ? response.folders.filter(f => f.id > 0).map(folder => ({
            ...folder,
            activity_id: this.currentIdActivity,
            visible: true,
            type: 'folder'
            }))
          : [];

        // Merge de subactividades y document folders (las carpetas se traen
        // completas; no se paginan).
        this.folders = [...subactivitiesMapped, ...documentFoldersMapped];

        this.folderFiles = files.map(item => ({
          ...item,
          visible: true
        }));

      } catch (error) {
        const message = error.response?.data?.message || error.response?.data?.error || error.message || this.$t('unknown_error');
        this.$notify.error('error_loading_folder_content_message', { message });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Handler de scroll del body del modal: cuando falta poco para el fondo,
     * trae la siguiente página de archivos (carga diferida).
     */
    onBodyScroll(e) {
      const el = e && e.target;
      if (!el) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
        this.loadMoreFiles();
      }
    },

    /**
     * Trae y agrega la siguiente página de archivos de la carpeta actual.
     * No hace nada en el root (sin carpeta abierta) ni si ya están todos.
     */
    async loadMoreFiles() {
      if (this.loadingMore) return;
      if (!this.currentDocFolder) return;                       // root: no hay archivos paginados
      if (this.folderFiles.length >= this.totalFiles) return;   // ya se cargaron todos

      try {
        this.loadingMore = true;
        const nextPage = this.filePage + 1;
        const response = await this.getDocuments(this.currentDocFolder, nextPage);
        const newFiles = (Array.isArray(response.documents) ? response.documents : [])
          .map(item => ({ ...item, visible: true }));

        this.folderFiles = this.folderFiles.concat(newFiles);
        this.filePage = nextPage;
        if (typeof response.total === 'number') this.totalFiles = response.total;

        // Si hay una búsqueda activa, aplicarla también a los recién agregados.
        if (this.searchQuery) this.performSearch(this.searchQuery);
      } catch (error) {
        const message = error.response?.data?.message || error.response?.data?.error || error.message || this.$t('unknown_error');
        this.$notify.error('error_loading_more_files_message', { message });
      } finally {
        this.loadingMore = false;
      }
    },

    goToBreadcrumb(index) {
      this.errorMessage = null; // Limpiar errores al navegar
      if (index === -1) {
        // Volver al root
        this.breadcrumbs = [];
        // En el root no hay archivos paginados: reset del estado de carga diferida.
        this.currentDocFolder = null;
        this.totalFiles = 0;
        this.filePage = 1;
        if (this.activityImage.type === 'drawing') {
          this.folders = [];
          this.folderFiles = this.drawings.map(drawing => ({
            ...drawing,
            visible: true
          }));
        } else {
          this.root.forEach(f => {
            f.visible = true; // Asegurarse de que todas las carpetas sean visibles
          });
          this.folders = this.root;
          this.folderFiles = []
        }

      } else {
        const breadcrumb = this.breadcrumbs.slice(0, index + 1);
        const targetFolder = breadcrumb[index];
        this.breadcrumbs = breadcrumb;

        this.openFolder(targetFolder)
      }
    },

    async getSubactivities(activityId){
      try {
        const { data } = await axios.get(`/api/v2/activity/${activityId}/subactivities`);
        if (data.success) {
          return data.subactivities;
        } else {
          this.$notify.error('error_loading_data');
          return [];
        }
      } catch (error) {
        const message = error.response?.data?.message || error.response?.data?.error || error.message || this.$t('unknown_error');
        this.$notify.error('error_loading_subactivities_message', { message });
        return [];
      }
    },

    async getDocuments(folder, page = 1){
      try {
        const activityId = folder.type == 'folder' ? this.currentIdActivity : folder.id;
        const params = {
          with_folders: 1,
          // El backend filtra a solo imágenes y PDFs, así la paginación y el
          // `total` reflejan exactamente lo que la UI muestra.
          only_images_pdf: 1,
          per_page: this.filePerPage,
          page,
        };
        if (folder.type == 'folder') {
          params.folder_id = folder.id;
        }
        const { data } = await axios.get(`/api/v2/activity/${activityId}/documents`, { params: params });
        if (data.success) {
          return data;
        } else {
          this.$notify.error('error_loading_data');
          return {
            documents: [],
            folders: [],
            total: 0
          };
        }
      } catch (error) {
        const message = error.response?.data?.message || error.response?.data?.error || error.message || this.$t('unknown_error');
        this.$notify.error('error_loading_documents_message', { message });
        return {
          documents: [],
          folders: [],
          total: 0
        };
      }
    },

    async saveActivityImage() {
      if (! this.selectedFile) { return false }

      if (this.activityImage.type == 'drawing' || this.activityImage.type == 'drawingActivity') {
        this.report_sections[this.activityImage.tabIndex].drawing_image = this.selectedFile.file.big_thumbnail
      } else if (this.activityImage.type == 'activity') {
        this.report_sections[this.activityImage.tabIndex].activity_image = this.selectedFile.file.big_thumbnail
      } else {
        // Origen: ruta de navegación (actividad › subactividad › …) para mostrar de dónde viene.
        const sourceName = (this.breadcrumbs || []).map((c) => c.name).filter(Boolean).join(' / ')
        this.report_sections[this.activityImage.tabIndex].images[this.activityImage.imageIndex] = {
          image: this.selectedFile.file.big_thumbnail,
          name: this.selectedFile.name,
          is_activity_document: true,
          source_name: sourceName,
          key: ''
        }
      }

      this.$emit('epc-select-success', this.report_sections)

      this.hide();
    },

    show(data) {
      this.activityImage = data;

      this.isModalActive = true;
    },

    resetModal() {
      this.activityImage = {
        tabIndex: 0,
        imageIndex: 0,
        url: '',
        activity_id: null,
        subactivity_id: null,
        document_id: null,
        subactivities: [],
        documents: [],
        type: 'image',
      }

      this.breadcrumbs = [];
      this.folders = this.root;
      this.folderFiles = [];
      this.searchQuery = '';
      this.selectedFile = null;
      this.errorMessage = null;

      // Reset del estado de carga diferida.
      this.filePage = 1;
      this.totalFiles = 0;
      this.loadingMore = false;
      this.currentDocFolder = null;
    },

    hide() {
      this.isModalActive = false;
    }
  }
};
</script>
<style scoped>
/* Oculta los botones para aumentar los inputs numéricos */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  /* display: none; <- Crashes Chrome on hover */
  -webkit-appearance: none;
  margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
}

input[type="number"] {
  -moz-appearance: textfield; /* Firefox */
}

/* Estilos para modal-card de Buefy - Reset y personalización */
::v-deep .modal-card {
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0.5rem;
  border: 1px solid #E4E6EB;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

::v-deep .modal-card-head {
  background-color: white;
  border-bottom: 1px solid #E4E6EB;
  border-radius: 0.5rem 0.5rem 0 0;
  padding: 1rem 1.5rem;
  flex-shrink: 0;
}

::v-deep .modal-card-body {
  flex: 1;
  overflow-y: auto;
  background-color: white;
  padding: 0;
}

::v-deep .modal-card-foot {
  background-color: white;
  border-top: 1px solid #E4E6EB;
  border-radius: 0 0 0.5rem 0.5rem;
  padding: 1rem 1.5rem;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
}
</style>
